import "@/App.css";
import React, { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  Mail, Phone, MessageCircle, Instagram, Headphones,
  ShieldCheck, Heart, MapPin, ChevronRight, Send, Lock, Calendar
} from "lucide-react";

import { Footer } from "./Home";


// Same venue imagery used on Locations page
const VENUE_IMAGES = [
  "https://images.unsplash.com/photo-1542204165-65bf26472b9b?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1524985069026-dd778a71c7b4?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?auto=format&fit=crop&w=1200&q=80",
];

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

const NavLink = ({ href, children, active }) => (
  <a
    href={href}
    className={`text-sm tracking-wider transition ${active ? "text-[#f4d089] border-b border-[#f4d089] pb-1" : "text-[#cbbf9f] hover:text-[#f4d089]"}`}
  >
    {children}
  </a>
);



const SUBJECTS = ["General Enquiry", "Booking Help", "Refund / Payment", "Group / Event", "Franchise", "Feedback"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [contact, setContact] = useState(null);

  const [branches, setBranches] = useState([]);
const [cities, setCities] = useState([]);

useEffect(() => {
  axios.get(`${API}/branches`).then((r) => setBranches(r.data || [])).catch(() => {});
  axios.get(`${API}/cities`).then((r) => setCities(r.data || [])).catch(() => {});
}, []);

  useEffect(() => {
  axios.get(`${API}/content/contact`)
    .then(r => setContact(r.data))
    .catch(() => {});
}, []);

const cityMap = React.useMemo(() => {
  const m = {};
  cities.forEach((c) => (m[c.id] = c.name));
  return m;
}, [cities]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact/messages`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      setSent(true);
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      alert("Could not send your message. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0604] text-[#e8dcc0]" data-testid="contact-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <a href="/" className="flex items-center" data-testid="contact-logo">
  <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
    <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
  </div>
</a>
          <nav className="hidden md:flex items-center gap-7">
            <NavLink href="/">Home</NavLink>
            <NavLink href="/experiences">Experiences</NavLink>
            <NavLink href="/book">Rooms</NavLink>
            <NavLink href="/locations">Locations</NavLink>
            <NavLink href="/packages">Offers</NavLink>
            <NavLink href="/about">About Us</NavLink>
            <NavLink href="/contact" active>Contact Us</NavLink>
          </nav>
          <GoldButton onClick={() => (window.location.href = "/book")} className="text-sm px-5 py-2.5 flex items-center gap-2">
            <Calendar size={16} /> Book Now
          </GoldButton>
        </Section>
      </header>

      {/* Hero */}
      <Section className="pt-12 pb-10 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-[11px] tracking-[0.2em] text-[#f4d089]">WE’D LOVE TO HEAR FROM YOU</div>
          <h1 className="font-display text-white text-4xl sm:text-6xl mt-4 leading-tight">
            Contact <span className="text-[#f4d089]">Us</span>
          </h1>
          <p className="text-[#bfb393] mt-5 max-w-xl text-[15px] leading-relaxed">
            Have a question, need help with a booking, or want to plan something special?
            <br />Our team is here to assist you.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: <Headphones size={20} className="text-[#f4d089]" />, t: "Quick Support", d: "We reply within 30 minutes" },
              { icon: <ShieldCheck size={20} className="text-[#f4d089]" />, t: "100% Privacy", d: "Your information is safe with us" },
              { icon: <Heart size={20} className="text-[#f4d089]" />,      t: "Customer First", d: "We’re here to make your experience perfect" },
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full border border-[#d4a574]/30 flex items-center justify-center shrink-0">{b.icon}</div>
                <div>
                  <div className="text-white text-sm font-semibold">{b.t}</div>
                  <div className="text-[#bfb393] text-xs leading-snug">{b.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border border-[#3a2a14]/70 h-[260px] sm:h-[420px] relative">
          <img src="/images/hero.jpg" alt="CineVibe interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-[#0a0604]/30 to-transparent" />
        </div>
      </Section>

      {/* Form + Other ways */}
      <Section className="grid lg:grid-cols-2 gap-6 pb-16">
        {/* Form */}
        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <Mail size={20} className="text-[#f4d089]" />
            <h2 className="font-display text-white text-2xl">Send Us a Message</h2>
          </div>
          <p className="text-[#bfb393] text-sm mb-6">Fill in the details below and we’ll get back to you.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs tracking-wider text-[#cbbf9f]">Full Name *</label>
                <input required value={form.name} onChange={update("name")} placeholder="Enter your full name"
                  className="mt-2 w-full bg-[#0d0905] border border-[#3a2a14] rounded-md px-4 py-3 text-[#e8dcc0] placeholder:text-[#6e6244] focus:outline-none focus:border-[#d4a574]" />
              </div>
              <div>
                <label className="text-xs tracking-wider text-[#cbbf9f]">Phone Number *</label>
                <div className="relative mt-2">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a89878]" />
                  <input required value={form.phone} onChange={update("phone")} placeholder="Enter 10 digit number"
                    className="w-full bg-[#0d0905] border border-[#3a2a14] rounded-md pl-10 pr-4 py-3 text-[#e8dcc0] placeholder:text-[#6e6244] focus:outline-none focus:border-[#d4a574]" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs tracking-wider text-[#cbbf9f]">Email Address *</label>
              <div className="relative mt-2">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a89878]" />
                <input required type="email" value={form.email} onChange={update("email")} placeholder="Enter your email address"
                  className="w-full bg-[#0d0905] border border-[#3a2a14] rounded-md pl-10 pr-4 py-3 text-[#e8dcc0] placeholder:text-[#6e6244] focus:outline-none focus:border-[#d4a574]" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-wider text-[#cbbf9f]">Subject *</label>
              <select required value={form.subject} onChange={update("subject")}
                className="mt-2 w-full bg-[#0d0905] border border-[#3a2a14] rounded-md px-4 py-3 text-[#e8dcc0] focus:outline-none focus:border-[#d4a574]">
                <option value="">Select a subject</option>
                {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-wider text-[#cbbf9f]">Message *</label>
              <textarea required maxLength={500} rows={5} value={form.message} onChange={update("message")} placeholder="Type your message here..."
                className="mt-2 w-full bg-[#0d0905] border border-[#3a2a14] rounded-md px-4 py-3 text-[#e8dcc0] placeholder:text-[#6e6244] focus:outline-none focus:border-[#d4a574] resize-none" />
              <div className="text-right text-[11px] text-[#a89878] mt-1">{form.message.length}/500</div>
            </div>
            <GoldButton type="submit" className="w-full flex items-center justify-center gap-2">
              <Send size={16} /> {sent ? "Message Sent ✓" : "Send Message"}
            </GoldButton>
            <p className="text-center text-xs text-[#a89878] flex items-center justify-center gap-1.5 pt-1">
              <Lock size={12} /> Your details are secure and will never be shared.
            </p>
          </form>
        </Card>

        {/* Other ways */}
        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <Headphones size={20} className="text-[#f4d089]" />
            <h2 className="font-display text-white text-2xl">Other Ways to Reach Us</h2>
          </div>
          <p className="text-[#bfb393] text-sm mb-6">Choose the most convenient way to connect with us.</p>

          {[
  { icon: <MessageCircle className="text-[#25D366]" size={22} />, t: "WhatsApp",  d: contact?.whatsapp_label  || "Chat with us instantly",        v: contact?.whatsapp_number_display || "+91 98765 43210", href: `https://wa.me/${(contact?.whatsapp_number_dial || "919876543210").replace(/\D/g,"")}` },
  { icon: <Phone className="text-[#f4d089]" size={22} />,        t: "Call Us",   d: contact?.call_label      || "Mon – Sun: 10:00 AM – 11:00 PM", v: contact?.call_number_display      || "+91 98765 43210", href: `tel:${contact?.call_number_dial || "+919876543210"}` },
  { icon: <Mail className="text-[#f4d089]" size={22} />,         t: "Email Us",  d: contact?.email_label     || "We reply within 30 minutes",    v: contact?.email                     || "hello@cinevibe.in", href: `mailto:${contact?.email || "hello@cinevibe.in"}` },
  { icon: <Instagram className="text-[#E1306C]" size={22} />,    t: "Instagram", d: contact?.instagram_label || "DM us on Instagram",            v: contact?.instagram_handle          || "@cinevibe.india",   href: contact?.instagram_url || "https://instagram.com/cinevibe.india" },
].map((row, i) => (
            <a key={i} href={row.href} target="_blank" rel="noreferrer"
              className="flex items-center gap-4 p-4 mb-3 rounded-lg border border-[#3a2a14]/70 hover:border-[#d4a574]/50 hover:bg-[#120c07] transition group">
              <div className="w-12 h-12 rounded-full bg-[#0d0905] border border-[#d4a574]/20 flex items-center justify-center shrink-0">
                {row.icon}
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold">{row.t}</div>
                <div className="text-[#bfb393] text-xs">{row.d}</div>
                <div className="text-[#f4d089] text-sm mt-0.5">{row.v}</div>
              </div>
              <ChevronRight size={18} className="text-[#a89878] group-hover:text-[#f4d089] transition" />
            </a>
          ))}
        </Card>
      </Section>

      {/* Locations */}
      <Section className="pb-16">
        <div className="text-center mb-8">
          <div className="text-[11px] tracking-[0.2em] text-[#f4d089]">OUR LOCATIONS</div>
          <h2 className="font-display text-white text-3xl sm:text-4xl mt-2">Find Us Near You</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
  {branches.length === 0 && (
    <div className="col-span-full text-center text-[#bfb393] text-sm py-6">Loading locations…</div>
  )}
  {branches.map((b, i) => (
    <Card key={b.id || i} className="overflow-hidden flex flex-col">
      <div className="relative h-36 bg-[#0d0905]">
        <img
          src={VENUE_IMAGES[i % VENUE_IMAGES.length]}
          alt={b.name}
          className="w-full h-full object-cover opacity-80"
        />
        {i === 0 && (
          <span className="absolute top-3 left-3 bg-[#1a0f06] border border-[#d4a574]/40 text-[#f4d089] text-[11px] tracking-wider px-2.5 py-1 rounded">
            Most Booked
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-white font-semibold">
          <MapPin size={16} className="text-[#f4d089]" />
          {b.name}{cityMap[b.city_id] ? `, ${cityMap[b.city_id]}` : ""}
        </div>
        <p className="text-[#bfb393] text-xs mt-2 leading-relaxed flex-1">
          {b.address || "Address coming soon"}
        </p>
        
        <a
          href={b.map_link || "#"}
          target="_blank"
          rel="noreferrer"
          className="mt-4 text-xs text-center tracking-wider border border-[#d4a574]/40 text-[#f4d089] rounded py-2 hover:bg-[#1a0f06]"
        >
          Get Directions
        </a>
      </div>
    </Card>
  ))}
</div>
        <div className="text-center mt-8">
          <a href="/locations" className="inline-flex items-center gap-2 text-[#f4d089] text-sm border border-[#d4a574]/40 px-5 py-2.5 rounded hover:bg-[#1a0f06]">
            <MapPin size={16} /> View All Locations
          </a>
        </div>
      </Section>

      

      <Footer />
    </div>
  );
};

export default Contact;
